// MapComponent.tsx
import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { defaultMapCenter } from '@/env/config'
import { MapResponse, MapRow, vehicleObject } from './Map'

// interface MapRowMachine {
//   id: string | undefined
//   name: string | undefined
// }

// interface MapRow {
//   imei: string
//   time: string // ISO 8601 string representation of DtServer (e.g., "2025-01-17T12:34:56Z")
//   lat: number  // Latitude as a floating-point number
//   lng: number  // Longitude as a floating-point number
//   altitude: number // Altitude in meters
//   angle: number // Angle in degrees
//   speed: number // Speed in meters per second
//   machine: MapRowMachine
// }

// interface MapResponse {
//   time: string // ISO 8601 string representation of the latest DtServer
//   rows: MapRow[] | null   // Array of MapRow objects
// }

function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
  if (!newResponse.rows) {
    return oldResponse
  }
  const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
  const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
  for (const newRow of newResponse.rows) {
    imeiMap.set(newRow.imei, newRow)
  }
  const mergedRows = Array.from(imeiMap.values());
  return {
    time: newResponse.time,
    rows: mergedRows,
  }
}


export const FailuresMapComponent = ({ services, session, fetchUrl}: {
  services: Services
  session: CurrentSession
  fetchUrl: string
}) => {
  const navigate = useNavigate()
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const navigation = useNavigate()

  async function refreshMap(fromDate?: Date, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate ? fromDate.toISOString() : ''}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse = await r.json()
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap().then(setMap)
  }, []);

  useEffect(() => {
    if (map === undefined) {
      return
    }
    const clock = async () => {
      if (map === undefined) {
        return
      }
      await refreshMap(map?.time ? new Date(map?.time) : undefined, map).then(setMap)
      handle = setTimeout(clock, 2000)
    }
    let handle: NodeJS.Timeout = setTimeout(clock, 2000)
    return () => {
      clearTimeout(handle)
    }
  }, [map])

  if (map === undefined) {
    return <div className='spinner'></div>
  }

  const firstRow = map?.rows?.length ? map.rows[0] : undefined;

  const center: LatLngExpression = myposition || (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || defaultMapCenter

  return (
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row) => (
        <Marker
          key={row.imei}
          position={row}
          icon={L.divIcon(vehicleObject(row))} >
          <Popup>
            <div onClick={() => row.machine.id && navigation(`/communal/machine/${row.machine.id}`)}>
              {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => navigator.clipboard.writeText(row.imei)}>{`IMEI: ${row.imei}`}</p> : null}
              {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null}
              <p>{session.translates.for('map.speed')}: {row.speed} km/h</p>
              <p>{session.translates.for('map.direction')}: {row.angle}°</p>
              {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
            </div>
          </Popup>
        </Marker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}
